import MapStackedItems from "@/components/mapping/MapStackedItems.vue";
import MarkerInfoWindow from "@/components/mapping/MarkerInfoWindow.vue";
import { MapState } from "@/store/map/types";
import {
	CloseClickResponse,
	ClusterContextMenuResponse,
	IInfoWindowOptions,
	MapEventListenerEventNames,
	MarkerContextMenuResponse
} from "@sureview/v2-mapping-saas";
import { Md5 } from "ts-md5";
import { Component, Vue, Watch } from "vue-property-decorator";
import { CombinedVueInstance } from "vue/types/vue";
import { namespace } from "vuex-class";

const MapStore = namespace("map");

/**
 * @summary Mixin which handles the map info windows.
 */
@Component
export default class MapInfoWindowMixin extends Vue {
    @MapStore.State((state: MapState) => state.markerContextMenuResponse) private readonly markerContextMenuResponse: MarkerContextMenuResponse | null;
    @MapStore.State((state: MapState) => state.clusterContextMenuResponse) public readonly clusterContextMenuResponse: ClusterContextMenuResponse | null;
    @MapStore.State((state: MapState) => state.closeInfoWindow) public readonly closeCurrentInfoWindow: boolean;
    @MapStore.Mutation protected setMarkerContextMenuResponse: (markerContextMenuResponse: MarkerContextMenuResponse | null) => void;
    @MapStore.Mutation protected setClusterContextMenuResponse: (clusterContextMenuResponse: ClusterContextMenuResponse | null) => void;
    @MapStore.Mutation protected setCloseInfoWindow: (close: boolean) => void;

    public readonly infoWindowPixelOffsetPin: number = -50;
    public readonly infoWindowPixelOffsetCentred: number = -30;

    private infoWindowComponent: CombinedVueInstance<Record<never, any> & Vue, object, object, object, Record<never, any>> | null = null;

    /**
     * @summary The info windows to be displayed on the map.
     */
    public infoWindowOptions: IInfoWindowOptions[] = [];

    /**
     * @summary Handler for changes to markerContextMenuResponse.
     */
    @Watch("markerContextMenuResponse")
    private onMarkerContextMenuResponseChanged(): void {
        if (this.markerContextMenuResponse) {
            this.onContextMenu();
        }
    }

    /**
     * @summary Handler for changes to clusterContextMenuResponse.
     */
    @Watch("clusterContextMenuResponse")
    private onClusterContextMenuResponseChanged(): void {
        if (this.clusterContextMenuResponse) {
            this.onClusterContextMenu();
        }
    }

    /**
     * @summary Handler for changes to closeCurrentInfoWindow.
     */
    @Watch("closeCurrentInfoWindow")
    private onCloseCurrentInfoWindowChanged(): void {
        if (this.closeCurrentInfoWindow) {
            this.closeInfoWindow();
        }
    }

    /**
     * @summary Handler for when contextmenu event is raised by a map marker.
     */
    public onContextMenu(): void {
        let markerInfoWindowConstructor = Vue.extend(MarkerInfoWindow);
        this.infoWindowComponent = new markerInfoWindowConstructor({
            store: this.$store,
            propsData: {
                itemInfo: {
                    mapLayerItemId: this.markerContextMenuResponse.mapLayerItemId,
                    mapLayerItemType: this.markerContextMenuResponse.mapLayerItemType,
                    objectId: this.markerContextMenuResponse.objectId,
                    title: this.markerContextMenuResponse.title
                }
            }
        }).$mount();

        this.infoWindowOptions = [
            {
                key: this.markerContextMenuResponse.key,
                content: this.infoWindowComponent.$el,
                position: this.markerContextMenuResponse.latLng,
                pixelOffset: {
                    width: 0,
                    height: this.infoWindowPixelOffsetPin,
                    equals: null
                },
                maxWidth: 250,
                mapEventListeners: [
                    // CloseClick triggers when the info window is closed. We need to tell the map to remove the info window from its service when closed.
                    {
                        eventName: MapEventListenerEventNames.CloseClick,
                        callback: (_: CloseClickResponse) => {
                            this.closeInfoWindow();
                            this.setMarkerContextMenuResponse(null);
                        },
                    },
                ],
            },
        ];
    }

    /**
     * @summary Handler for when contextMenu event is raised by a map cluster.
     */
    private onClusterContextMenu(): void {
        let mapStackedItemsConstructor = Vue.extend(MapStackedItems);
        this.infoWindowComponent = new mapStackedItemsConstructor({
            store: this.$store,
        }).$mount();

        let headerElement = document.createElement("h4");
        headerElement.style.paddingLeft = "10px";
        headerElement.textContent = "Contents of cluster";

        this.infoWindowOptions = [
            {
                key: Md5.hashStr(JSON.stringify(this.clusterContextMenuResponse)),
                headerContent: headerElement,
                content: this.infoWindowComponent.$el,
                position: this.clusterContextMenuResponse.latLng,
                pixelOffset: {
                    width: 0,
                    height: this.infoWindowPixelOffsetCentred,
                    equals: null
                },
                maxWidth: 250,
                mapEventListeners: [
                    // CloseClick triggers when the info window is closed. We need to tell the map to remove the info window from its service when closed.
                    {
                        eventName: MapEventListenerEventNames.CloseClick,
                        callback: (_: CloseClickResponse) => {
                            this.closeInfoWindow();
                            this.setClusterContextMenuResponse(null);
                        },
                    },
                ],
            },
        ];
    }

    /**
     * @summary Closes the info window(s).
     */
    public closeInfoWindow(): void {
        this.infoWindowComponent?.$destroy();
        this.infoWindowComponent = null;
        this.infoWindowOptions = [];
        this.setCloseInfoWindow(false);
    }
}
